// Mocked offering constants for Phase 2.4b Subscribe widgets.
//
// Phase 6 replaces this with a /dossier/restaurants/:id/offering.yaml
// reader. Until then, every Subscribe widget reads from this single
// source so the demo numbers stay consistent across panels.
//
// The shape mirrors what the offering.yaml schema will surface (per the
// metagraph at meta/offerings/_lifecycle.yaml): price, bounds, lockup,
// distribution cadence, ticker, contract addresses, escrow, fee.

export type SubscribeOfferingMock = {
  rmintId: string;
  restaurantName: string;
  ticker: string;
  offeringId: string;
  pricePerToken: number;
  minAmountUsdc: number;
  maxAmountUsdc: number;
  lockupMonths: number;
  lockupEndIso: string;
  distributionCadence: 'quarterly' | 'monthly' | 'annual';
  distributionUnit: 'USDC';
  networkFeeUsdcEst: number;
  estimatedFirstDistributionIso: string;
  saleContractAddress: `0x${string}`;
  tokenContractAddress: `0x${string}`;
  signByDeadlineIso: string;
};

export const SAKURA_RAMEN_NC: SubscribeOfferingMock = {
  rmintId: 'rest_demo_sakura',
  restaurantName: 'Sakura Ramen NC',
  ticker: 'RMOT-SAK',
  offeringId: 'offering_2024_03',
  pricePerToken: 1.0,
  minAmountUsdc: 5000,
  maxAmountUsdc: 50000,
  lockupMonths: 36,
  lockupEndIso: '2029-04-01',
  distributionCadence: 'quarterly',
  distributionUnit: 'USDC',
  networkFeeUsdcEst: 0.42,
  estimatedFirstDistributionIso: '2026-07-01',
  saleContractAddress: '0xe6CF06892E68A0f723538FA385AFD6C904f9d0C4',
  tokenContractAddress: '0x4a2bC3D5eF6789ABcDeF0123456789AbCdEf1cC2',
  signByDeadlineIso: '2026-06-04',
};

// Mocked wallet USDC balance shown in the Fund widget. Phase 6 reads
// the live ERC-20 balance via viem.
export const MOCK_USDC_BALANCE = 47200;

// Mocked default subscription amount until Phase 6 reads
// subscription.allocation.amountUsdc from the dossier. Both the
// allocate widget (initial picker value) and the fund widget (order
// summary) read from this constant so demo numbers stay consistent.
export const MOCK_SUBSCRIPTION_AMOUNT_USDC = 12500;

// Mocked tx hashes for the two-tx Fund flow + Mint confirmation.
// Phase 6: real hashes come from viem writeContract returns.
export const MOCK_TX_HASHES = {
  approve: '0x8a4f5c20e000000000000000000000000000000000000000000000000000c20e' as `0x${string}`,
  purchase: '0x9b5ed31f0000000000000000000000000000000000000000000000000000d31f' as `0x${string}`,
  mint: '0xab12cd34ef560000000000000000000000000000000000000000000000ef5678' as `0x${string}`,
};

export const MOCK_OFFERING_BY_SUBSCRIPTION: Record<string, SubscribeOfferingMock> = {
  // Default fallback — every subscriptionId in 2.4b resolves to Sakura.
  // Phase 6: populated dynamically from subscription.subject.rmintId.
  default: SAKURA_RAMEN_NC,
};

export function getOfferingForSubscription(_subscriptionId: string): SubscribeOfferingMock {
  return MOCK_OFFERING_BY_SUBSCRIPTION.default!;
}
